import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { orgsApi, type Organization, type OrgMember, type InviteResult, ApiError } from "@/api/client";
import { AppShell } from "@/components/AppShell";

function short(addr: string): string {
  return addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : "—";
}

export default function OrganizationPage() {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [active, setActive] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // convite
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [inviting, setInviting] = useState(false);
  const [invite, setInvite] = useState<InviteResult | null>(null);

  // criação de organização
  const [novoNome, setNovoNome] = useState("");
  const [criando, setCriando] = useState(false);

  useEffect(() => {
    orgsApi.mine()
      .then((r) => {
        setOrgs(r.organizations);
        if (r.organizations.length > 0) setActive(r.organizations[0]);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!active) return;
    orgsApi.members(active.id)
      .then((r) => setMembers(r.members))
      .catch((err) => setError(err instanceof ApiError ? err.message : err.message));
  }, [active]);

  async function onInvite(e: FormEvent) {
    e.preventDefault();
    if (!active) return;
    setInviting(true);
    setError(null);
    setInvite(null);
    try {
      const r = await orgsApi.invite(active.id, email, nome);
      setInvite(r);
      setEmail(""); setNome("");
      orgsApi.members(active.id).then((m) => setMembers(m.members));
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : err.message ?? "Erro");
    } finally {
      setInviting(false);
    }
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setCriando(true);
    setError(null);
    try {
      const nova = await orgsApi.create(novoNome.trim());
      setOrgs((prev) => [...prev, nova]);
      setActive(nova);
      setNovoNome("");
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : err.message ?? "Erro");
    } finally {
      setCriando(false);
    }
  }

  const isOwner = active?.role === "OWNER";
  const inviteLink = invite?.code
    ? `${window.location.origin}/ativar?codigo=${invite.code}`
    : null;

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto p-6">
        <Link to="/dashboard" className="text-sm underline text-app-muted hover:text-app-accent">← dashboard</Link>
        <div className="flex items-center justify-between my-4 gap-4">
          <h1 className="text-2xl font-bold">Organização</h1>
          {orgs.length > 1 && (
            <select value={active?.id ?? ""}
                    onChange={(e) => setActive(orgs.find((o) => o.id === e.target.value) ?? null)}
                    className="px-3 py-2 text-sm">
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.nome}</option>)}
            </select>
          )}
        </div>

        {loading && <p className="text-sm text-app-muted">Carregando...</p>}
        {error && <div className="bg-red-500/10 border border-red-500/40 rounded p-3 text-sm text-red-500 mb-3">{error}</div>}

        {!loading && orgs.length === 0 && (
          <section className="app-card p-4">
            <h2 className="font-semibold mb-1">Você ainda não tem uma organização</h2>
            <p className="text-xs text-app-muted mb-3">
              A organização é o grupo onde vivem os membros, projetos e a governança.
              Crie a primeira — você vira o dono e poderá convidar os outros.
            </p>
            <form onSubmit={onCreate} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
              <div>
                <label className="block text-xs font-medium mb-1">Nome da organização</label>
                <input value={novoNome} onChange={(e) => setNovoNome(e.target.value)} required
                       className="w-full px-3 py-2 text-sm" placeholder="ex: Goyaz Games" />
              </div>
              <button type="submit" disabled={criando}
                      className="bg-app-accent text-app-accent-text rounded px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50">
                {criando ? "..." : "Criar organização"}
              </button>
            </form>
          </section>
        )}

        {active && (
          <>
            <p className="text-sm text-app-muted mb-4">
              <strong>{active.nome}</strong> · você é <strong>{active.role === "OWNER" ? "dono" : "membro"}</strong>
            </p>

            <section className="app-card overflow-hidden mb-6">
              <table className="w-full text-sm">
                <thead className="bg-app-surface-2">
                  <tr className="border-b border-app-border text-left text-xs uppercase text-app-muted">
                    <th className="px-3 py-2">Membro</th>
                    <th>E-mail</th>
                    <th>Carteira</th>
                    <th>Papel</th>
                    <th className="pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.socioId} className="border-b border-app-border last:border-0">
                      <td className="px-3 py-2 font-medium">{m.nome}</td>
                      <td className="text-app-muted">{m.email || "—"}</td>
                      <td className="font-mono text-xs">{short(m.smartAccount)}</td>
                      <td>{m.role === "OWNER" ? "Dono" : "Membro"}</td>
                      <td className="pr-3">
                        {m.activated
                          ? <span className="text-green-500 text-xs">✓ ativo</span>
                          : <span className="text-amber-500 text-xs">convidado</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {isOwner && (
              <section className="app-card p-4">
                <h2 className="font-semibold mb-1">Convidar pro grupo</h2>
                <p className="text-xs text-app-muted mb-3">
                  Por e-mail. Se a pessoa já tem conta, entra direto. Se não, geramos um
                  código/link pra ela criar a conta e entrar.
                </p>
                <form onSubmit={onInvite} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                  <div>
                    <label className="block text-xs font-medium mb-1">Nome</label>
                    <input value={nome} onChange={(e) => setNome(e.target.value)} required
                           className="w-full px-3 py-2 text-sm" placeholder="ex: Hiro" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">E-mail</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                           className="w-full px-3 py-2 text-sm" placeholder="hiro@exemplo.com" />
                  </div>
                  <button type="submit" disabled={inviting}
                          className="bg-app-accent text-app-accent-text rounded px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50">
                    {inviting ? "..." : "Convidar"}
                  </button>
                </form>

                {invite && invite.status === "added" && (
                  <div className="mt-3 bg-green-500/10 border border-green-500/40 rounded p-3 text-sm text-green-600">
                    ✓ <strong>{invite.nome}</strong> já tinha conta e entrou no grupo.
                  </div>
                )}
                {invite && invite.status === "invited" && inviteLink && (
                  <div className="mt-3 bg-amber-500/10 border border-amber-500/40 rounded p-3 text-sm">
                    🎟️ Convite criado pra <strong>{invite.nome}</strong>. Envie o link (ou o código):
                    <div className="flex items-center gap-2 mt-2">
                      <code className="flex-1 bg-app-surface-2 rounded px-2 py-1 text-xs truncate">{inviteLink}</code>
                      <button type="button" onClick={() => navigator.clipboard.writeText(inviteLink)}
                              className="text-xs border border-app-border rounded px-2 py-1 hover:bg-app-surface-2">copiar</button>
                    </div>
                    <div className="text-xs text-app-muted mt-1">Código: <code className="font-mono">{invite.code}</code></div>
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
